import React, { useState } from "react";
import { gql, useQuery } from "@apollo/client";
import classNames from "classnames";
import styles from "./index.module.scss";
import { Heading, Text } from "../../../Type";
import TextLink from "../../../TextLinks";
import { useClientFileContext } from "../../../../contexts/ClientFileProvider";
import { CircleChecked, CircleCheckedEmpty } from "../../../Icons/general-interface-2";
import RoleLabel from "../../../UserInterface/RoleLabel";
import { isAuthorizer, isSalesRep } from "../../../../utils/user";
import { useStoreContext } from "../../../../contexts/StoreProvider";
import Button from "../../../Button";
import { ArrowRight } from "../../../Icons/arrows";
import Countdown from "../../../Countdown";

const GET_FILE_SIGNATURES = gql`
  query GetClientFileSignatures($id: ID!) {
    clientFileSignatures(clientFileId: $id) {
      signatures {
        roleName
        name
        email
        me
        status
      }
      signingLink {
        url
        expiry
      }
    }
  }
`;

type Signer = {
  roleName: string;
  name: string;
  email: string;
  me: boolean;
  status: string;
};

const StatusScreen = () => {
  const {
    state: { user },
  } = useStoreContext();
  const { clientFile } = useClientFileContext();
  const { status } = clientFile;
  const name = `${clientFile.client.first_name} ${clientFile.client.last_name}`;
  const signingAuthName = clientFile.signing_authorizer
    ? `${clientFile.signing_authorizer.first_name} ${clientFile.signing_authorizer.last_name}`
    : "";
  const [signingLinkStatus, setSigningLinkStatus] = useState("");

  // Get the DocuSign event, if any
  const urlParams = new URLSearchParams(window.location.search);
  const event = urlParams.get("event");
  // If the rep has just returned from signing the form
  const signingFinalised = event === "signing_complete" && isSalesRep(user);

  // Get signer status
  const envelopeExists = !!clientFile.ds_envelope_id;
  const isSignStep = status === "PendingSignature" && envelopeExists && !signingFinalised;
  const { loading, error, data, refetch } = useQuery(GET_FILE_SIGNATURES, {
    fetchPolicy: "network-only",
    variables: {
      id: clientFile.id,
    },
    skip: !isSignStep,
  });

  const renderSignatures = () => {
    if (loading)
      return <p className={styles.signaturesLoading}>Getting signatures...</p>;
    if (error)
      return <p className={styles.signaturesError}>Error getting signatures</p>;

    const signatures = data?.clientFileSignatures?.signatures;
    if (!signatures?.length) return null;

    const roleTitle = (roleName: string) => {
      switch (roleName) {
        case "authorizer":
          return "Authorizer";
        case "vendor":
          return "Sales Rep";
        default:
          return "";
      }
    };

    const roleLabel = (roleName: string) => {
      const title = roleTitle(roleName);
      if (!title) return null;
      return <RoleLabel userRole={title} />;
    };

    const icon = (status: string) => {
      return status === "completed" ? (
        <CircleChecked className={styles.icon} />
      ) : (
        <CircleCheckedEmpty className={styles.icon} />
      );
    };

    return (
      <ul>
        {signatures.map((signer: Signer, i) => {
          let status = signer.status === "completed" ? "completed" : "later";
          if (
            status !== "completed" &&
            (i === 0 || signatures[i - 1].status === "completed")
          ) {
            status = "next";
          }
          return (
            <li
              key={signer.email}
              className={classNames(styles[status], styles[signer.roleName])}
            >
              {icon(signer.status)}{" "}
              <span className={styles.name}>{signer.name}</span>{" "}
              {roleLabel(signer.roleName)}
            </li>
          );
        })}
      </ul>
    );
  };

  // Render status screen. First match will be rendered.
  const progressSummary = [
    {
      condition: status === "Review",
      heading: "Application form submitted to Motion for review",
      contentLines: [
        `${name}’s application will be reviewed by a Motion team member. Edits will be locked.`,
        "Once the form has been reviewed, you will receive a notification.",
      ],
    },
    {
      condition: status === "Action",
      heading: "Application form submitted to Authorizer for edits",
      contentLines: [
        `${name}’s application will be reviewed by their Authorizer.`,
        "Once the form has been reviewed, you will receive a notification.",
      ],
    },
    {
      condition: signingFinalised,
      heading: "Application form is being processed",
      contentLines: [
        `${name}’s application has been signed by all parties.`,
        "Please wait a moment while we finish processing the application.",
      ],
    },
    {
      condition: status === "PendingSignature" && !isSignStep && signingAuthName !== "" && isSalesRep(user),
      heading: "Application form submitted to Authorizer for signature",
      contentLines: [
        `${name}’s application has been reviewed by Motion.`,
        `The form is waiting for signing to be initiated by the Authorizer${
          signingAuthName ? ` (${signingAuthName})` : "."
        }.`,
      ],
    },
    {
      condition: status === "PendingSignature" && !isSignStep && signingAuthName !== "" && isAuthorizer(user),
      heading: "Application form submitted to Authorizer for signature",
      contentLines: [
        `${name}’s application has been submitted to Authorizer for signatures.`,
        `The form is waiting for signing to be initiated by the Authorizer${
          signingAuthName ? ` (${signingAuthName})` : "."
        }.`,
      ],
    },
    {
      condition: status === "PendingSignature" && !isSignStep && signingAuthName === "",
      heading: "Application form submitted for signature",
      contentLines: [
        `${name}’s application has been submitted for signatures.`,
        `The form is waiting for signing to be initiated by the Authorizer.`
      ],
    },
    {
      condition: event === "signing_complete" && isAuthorizer(user),
      heading: "Application form submitted to Motion for signature",
      contentLines: [
        `${name}’s application will be signed by a Motion team member.`,
        "Once the form has been signed, you will receive a notification.",
      ],
    },
    {
      condition: status === "PendingSignature",
      heading: "Application form is pending signatures",
      contentLines: [
        `${name}’s form is waiting for signatures and can only be submitted after all signatures are acquired.`,
      ],
    },
    {
      condition: status === "NotStarted" || status === "Progress",
      heading: "Application form not submitted for review",
      contentLines: [
        `${name}’s application has not been submitted for review yet.`,
        "Once the form is ready for review, you will receive a notification.",
      ],
    },
    {
      condition: event === "skip_signing" && isAuthorizer(user),
      heading: "Application form submitted to Motion for signature",
      contentLines: [
        `${name}’s application will be signed by a Motion team member.`,
        "Once the form has been signed, you will receive a notification.",
      ],
    },
    {
      // Default. We shouldn't get to this point.
      condition: true,
      heading: `Application status: ${status}`,
      contentLines: [],
    },
  ].find((summary) => summary.condition);

  const signingLink = data?.clientFileSignatures?.signingLink;

  const signingButtonLabel = () => {
    switch (signingLinkStatus) {
      case "error":
        return "Error getting signing url";
      case "loading":
        return "Loading...";
      default:
        return "Sign now";
    }
  };

  return (
    <div>
      <div>
        <Heading className={styles.topHeading} size="h3">
          {progressSummary?.heading}
        </Heading>
        <p className={styles.txtStyle}>
          {progressSummary?.contentLines?.map((line, index) => {
            return (
              <React.Fragment key={index.toString()}>
                {line}
                <br />
              </React.Fragment>
            );
          })}
        </p>
        {isSignStep && (
          <div className={styles.signers}>{renderSignatures()}</div>
        )}
        {signingLink && (
          <div className={styles.signNow}>
            <Button
              onClick={() => {
                setSigningLinkStatus("loading");
                if (new Date(signingLink.expiry) > new Date()) {
                  // Note: DocuSign sometimes takes a long time (2 minutes) to respond
                  // Could be rate limiting?
                  window.location.href = signingLink.url;
                } else {
                  refetch().then((response) => {
                    const signingUrl =
                      response?.data?.clientFileSignatures?.signingLink?.url;
                    if (signingUrl) {
                      window.location.href = signingUrl;
                    } else {
                      setSigningLinkStatus("error");
                    }
                  });
                }
              }}
              primary
              icon={<ArrowRight />}
              disabled={!!signingLinkStatus}
            >
              {signingButtonLabel()}
            </Button>
          </div>
        )}
        {signingFinalised && (
          <Text fade className={styles.countdown}>
            The page will refresh in{" "}
            <span className={styles.seconds}>
              <Countdown
                seconds={40}
                onTimerEnd={() => {
                  window.location.href = window.location.href.split("?")[0];
                }}
              />
            </span>{" "}
            seconds.
          </Text>
        )}
      </div>
      <div className={styles.btnStyle}>
        <TextLink to="/dashboard">Return to dashboard</TextLink>
      </div>
    </div>
  );
};

export default StatusScreen;
